// utils/adminLogs.js
// Bug and Enhancement Tracking System
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";

const LOGS_COLLECTION = "adminLogs";
const COUNTERS_DOC = "counters";

/**
 * Get the next sequential ID for a bug or enhancement
 */
export async function getNextLogId(type) {
  try {
    const countersRef = doc(db, LOGS_COLLECTION, COUNTERS_DOC);
    const countersSnap = await getDoc(countersRef);
    
    const currentCounters = countersSnap.exists() 
      ? countersSnap.data() 
      : { bugCounter: 0, enhancementCounter: 0 };
    
    let nextId;
    let updateField;
    
    if (type === "bug") {
      currentCounters.bugCounter = (currentCounters.bugCounter || 0) + 1;
      nextId = `bug-${String(currentCounters.bugCounter).padStart(3, "0")}`;
      updateField = { bugCounter: currentCounters.bugCounter };
    } else if (type === "enhancement") {
      currentCounters.enhancementCounter = (currentCounters.enhancementCounter || 0) + 1;
      nextId = `enh-${String(currentCounters.enhancementCounter).padStart(3, "0")}`;
      updateField = { enhancementCounter: currentCounters.enhancementCounter };
    } else {
      throw new Error(`Invalid type: ${type}. Must be "bug" or "enhancement"`);
    }
    
    await setDoc(countersRef, updateField, { merge: true });
    return nextId;
  } catch (err) {
    console.error("Error getting next log ID:", err);
    throw err;
  }
}

/**
 * Create a new log entry
 */
export async function createLogEntry({
  type, // "bug" or "enhancement"
  title,
  description = "",
  status = type === "bug" ? "current" : "planned",
  priority = "medium",
  tags = [],
  createdBy = "system",
  conversationSessionId = null, // Optional: for grouping related conversation work
}) {
  try {
    const id = await getNextLogId(type);
    const now = serverTimestamp();
    const entry = {
      id,
      type,
      title,
      description,
      status,
      priority,
      tags: Array.isArray(tags) ? tags : [],
      createdAt: now,
      updatedAt: now,
      lastActivityAt: now, // Track when entry was last updated (for cross-session tracking)
      resolvedAt: null,
      resolvedInVersion: null,
      createdBy,
      notes: [],
      ...(conversationSessionId && { conversationSessionId }), // Only include if provided
    };
    
    const entryRef = doc(db, LOGS_COLLECTION, id);
    await setDoc(entryRef, entry);
    
    return { id, ...entry };
  } catch (err) {
    console.error("Error creating log entry:", err);
    throw err;
  }
}

/**
 * Update an existing log entry
 */
export async function updateLogEntry(entryId, updates) {
  try {
    const entryRef = doc(db, LOGS_COLLECTION, entryId);
    const now = serverTimestamp();
    const updateData = {
      ...updates,
      updatedAt: now,
      lastActivityAt: now, // Always update lastActivityAt on any change
    };
    
    // If status is being changed to resolved/completed, set resolvedAt
    if (updates.status === "resolved" || updates.status === "completed") {
      if (!updates.resolvedAt) {
        updateData.resolvedAt = now;
      }
    }
    
    await updateDoc(entryRef, updateData);
    return true;
  } catch (err) {
    console.error("Error updating log entry:", err);
    throw err;
  }
}

/**
 * Add a note to a log entry
 */
export async function addLogNote(entryId, noteText, statusChange = null) {
  try {
    const entryRef = doc(db, LOGS_COLLECTION, entryId);
    const entrySnap = await getDoc(entryRef);
    
    if (!entrySnap.exists()) {
      throw new Error(`Entry ${entryId} not found`);
    }
    
    const currentData = entrySnap.data();
    const notes = Array.isArray(currentData.notes) ? [...currentData.notes] : [];
    
    // Use client-side Date object for array items (Firestore doesn't support serverTimestamp() in arrays)
    notes.push({
      timestamp: new Date(), // Use client-side Date instead of serverTimestamp()
      text: noteText,
      statusChange: statusChange || null,
    });
    
    const now = serverTimestamp();
    await updateDoc(entryRef, {
      notes, // Update entire array
      updatedAt: now,
      lastActivityAt: now, // Update lastActivityAt when adding notes
    });
    
    return true;
  } catch (err) {
    console.error("Error adding log note:", err);
    throw err;
  }
}

/**
 * Get all log entries, optionally filtered
 */
export async function getAllLogs(filters = {}) {
  try {
    let q = query(collection(db, LOGS_COLLECTION), orderBy("createdAt", "desc"));
    
    // Apply filters
    if (filters.type) {
      q = query(q, where("type", "==", filters.type));
    }
    if (filters.status) {
      q = query(q, where("status", "==", filters.status));
    }
    
    const snapshot = await getDocs(q);
    const entries = [];
    
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      entries.push({
        ...data,
        // Convert Firestore timestamps to Date objects
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
        lastActivityAt: data.lastActivityAt?.toDate?.() || (data.lastActivityAt ? new Date(data.lastActivityAt) : data.updatedAt?.toDate?.() || new Date()),
        resolvedAt: data.resolvedAt?.toDate?.() || (data.resolvedAt ? new Date(data.resolvedAt) : null),
      });
    });
    
    return entries;
  } catch (err) {
    console.error("Error fetching log entries:", err);
    throw err;
  }
}

/**
 * Get log entries that were recently updated (for cross-session tracking)
 * @param {number} hours - Number of hours to look back (default: 24)
 * @param {string} maxStatus - Maximum status to include (e.g., "current", "planned") - excludes "resolved"/"completed"
 */
export async function getRecentUnresolvedIssues(hours = 24, maxStatus = null) {
  try {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - hours * 60 * 60 * 1000);
    
    // Get all unresolved entries
    const unresolvedStatuses = ["current", "investigating", "planned", "in-progress"];
    let entries = [];
    
    for (const status of unresolvedStatuses) {
      if (maxStatus && status > maxStatus) continue;
      const logs = await getAllLogs({ status });
      entries.push(...logs);
    }
    
    // Filter by lastActivityAt
    const recent = entries.filter((entry) => {
      const lastActivity = entry.lastActivityAt || entry.updatedAt || entry.createdAt;
      return lastActivity >= cutoffTime;
    });
    
    // Sort by lastActivityAt descending
    recent.sort((a, b) => {
      const aTime = a.lastActivityAt || a.updatedAt || a.createdAt;
      const bTime = b.lastActivityAt || b.updatedAt || b.createdAt;
      return bTime - aTime;
    });
    
    return recent;
  } catch (err) {
    console.error("Error fetching recent unresolved issues:", err);
    throw err;
  }
}

/**
 * Get a single log entry by ID
 */
export async function getLogEntry(entryId) {
  try {
    const entryRef = doc(db, LOGS_COLLECTION, entryId);
    const entrySnap = await getDoc(entryRef);
    
    if (!entrySnap.exists()) {
      return null;
    }
    
    const data = entrySnap.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
      resolvedAt: data.resolvedAt?.toDate?.() || (data.resolvedAt ? new Date(data.resolvedAt) : null),
    };
  } catch (err) {
    console.error("Error fetching log entry:", err);
    throw err;
  }
}

/**
 * Delete a log entry (Qwizard only)
 */
export async function deleteLogEntry(entryId) {
  try {
    const entryRef = doc(db, LOGS_COLLECTION, entryId);
    await deleteDoc(entryRef);
    return true;
  } catch (err) {
    console.error("Error deleting log entry:", err);
    throw err;
  }
}
